import { ServiceConfig, RetryConfig } from '../types/services';
import { ApiError } from '../types/api';

export abstract class BaseService {
  protected readonly name: string;
  protected readonly config: ServiceConfig;
  protected debug: { enabled: boolean; logLevel: string };

  constructor(name: string, config: ServiceConfig = {}) {
    this.name = name;
    this.config = {
      enableCircuitBreaker: true,
      enablePerformanceTracking: true,
      enableRetry: true,
      timeout: 30000,
      maxRetryAttempts: 3,
      retryBaseDelay: 1000,
      failureThreshold: 5,
      ...config
    };

    this.debug = {
      enabled: config.debug || false,
      logLevel: 'info'
    };
  }

  protected async executeOperation<T>(
    operation: () => Promise<T>,
    operationName: string,
    options: {
      metadata?: any;
      context?: any;
      retryConfig?: Partial<RetryConfig>;
    } = {}
  ): Promise<T> {
    const startTime = Date.now();
    
    try {
      this.logDebug(`Starting operation: ${operationName}`, {
        metadata: options.metadata,
        context: options.context
      });

      const result = await operation();
      
      const duration = Date.now() - startTime;
      this.logDebug(`Operation completed: ${operationName}`, {
        duration,
        success: true
      });

      return result;
    } catch (error) {
      const duration = Date.now() - startTime;
      this.logDebug(`Operation failed: ${operationName}`, {
        duration,
        error: error instanceof Error ? error.message : String(error)
      });

      // Convert to typed error
      const apiError = this.createApiError(error, operationName);
      throw apiError;
    }
  }

  protected createApiError(error: any, operationName: string): ApiError {
    if (error instanceof Error && 'status' in error) {
      return error as ApiError;
    }

    const apiError = new Error(
      `${operationName} failed: ${error instanceof Error ? error.message : String(error)}`
    ) as ApiError;

    if (error instanceof Error) {
      apiError.stack = error.stack;
    }

    return apiError;
  }

  protected logDebug(message: string, data?: any): void {
    if (!this.debug.enabled) return;
    
    console.log(`[${this.name}]`, {
      service: this.name,
      timestamp: new Date().toISOString(),
      message,
      ...(data && { data })
    });
  }

  protected enableDebug(): void {
    this.debug.enabled = true;
  }

  protected disableDebug(): void {
    this.debug.enabled = false;
  }

  // Abstract methods for subclasses to implement
  protected abstract onCircuitBreakerChange(eventType: string, data: any): void;
  protected abstract onRetry(error: Error, attempt: number, delay: number, operationName: string): void;
}
